import PropTypes from "prop-types";
import React from "react";

import { SMALL, useDeviceInfo } from "@swa-ui/browser";
import { Accordion, Heading, Link } from "@swa-ui/core";
import { HtmlValue } from "@swa-ui/placements";
import { classNames } from "@swa-ui/string";

import styles from "./ColumnList.module.scss";

/**
 * ColumnList generates list of options that are displayed in the DotcomFooter.
 */

export const ColumnList = (props) => {
  const { className, linkColumns } = props;
  const { screenSize } = useDeviceInfo();

  return linkColumns ? (
    <div className={classNames(styles.container, className)}>{linkColumns.map(renderColumn)}</div>
  ) : null;

  function renderColumn(columnInfo) {
    const { linkColumnHeader, linkColumnText } = columnInfo;
    const { value } = linkColumnHeader;

    return screenSize === SMALL ? (
      <Accordion {...getAccordionProps(linkColumnHeader, value)} key={value}>
        {renderList(linkColumnText, linkColumnHeader)}
      </Accordion>
    ) : (
      <div key={value}>
        <Heading {...getHeadingProps()}>{renderHeadingContent(linkColumnHeader)}</Heading>
        {renderList(linkColumnText)}
      </div>
    );
  }

  function renderHeadingContent(linkColumnHeader) {
    const { value } = linkColumnHeader;

    return (
      <>
        {screenSize === SMALL ? (
          <div className={styles.accordionHeader}>
            <HtmlValue htmlValue={value} />
          </div>
        ) : (
          <Link {...getHeaderLinkProps(linkColumnHeader)}>
            <HtmlValue htmlValue={value} />
          </Link>
        )}
      </>
    );
  }

  function renderList(linkColumnText, linkColumnHeader) {
    return (
      <ul className={styles.list}>
        {screenSize === SMALL && renderLinks(linkColumnHeader)}
        {linkColumnText.map(renderLinks)}
      </ul>
    );
  }

  function renderLinks(linkInfo) {
    const { value } = linkInfo;

    return (
      <li key={value}>
        <Link {...getLinkProps(linkInfo)}>
          <HtmlValue htmlValue={value} />
        </Link>
      </li>
    );
  }

  function getAccordionProps(linkColumnHeader, value) {
    return {
      label: renderHeadingContent(linkColumnHeader),
      name: value,
      noBackground: true,
      styleType: "primary",
    };
  }

  function getHeadingProps() {
    return {
      headingLevel: 5,
      spacing: "xSmall",
    };
  }

  function getLinkProps(linkInfo) {
    const { external, externalLink, href, newWindow, url } = linkInfo;
    const isExternal = external ?? externalLink;

    return {
      emphasis: false,
      external: isExternal,
      href: href ?? url,
      newWindow: newWindow ?? isExternal,
      size: "fontSize14",
    };
  }

  function getHeaderLinkProps(linkColumnHeader) {
    const { external, externalLink, href, url } = linkColumnHeader;
    const isNonLink = !(href || url);

    return {
      className: classNames({ [styles.nonLink]: isNonLink }),
      emphasis: true,
      external: external ?? externalLink,
      href: href ?? url,
      size: "fontSize16",
    };
  }
};

ColumnList.displayName = "ColumnList";
ColumnList.propTypes = {
  /**
   * Additional classes for positioning the component. Given classes may only position this component for layout
   * purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /** Link columns displayed in the footer. */
  linkColumns: PropTypes.array,
};
